import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import LicenseNavigatorV2Flow from '~/components/legacy/LicenseNavigatorV2Flow.jsx';
import EventNavigatorFlow from '~/components/legacy/EventNavigatorFlow.jsx';
import { parseReactProps } from '~/components/legacy/reactProps.js';

const SMART_NAVIGATOR_TOP_LEVEL_SERVICE_KEYS = ['content', 'spaces', 'events', 'courses', 'special_cases'];

function initialStateForServiceKey(serviceKey, hasEventBranch = false) {
  if (!SMART_NAVIGATOR_TOP_LEVEL_SERVICE_KEYS.includes(serviceKey)) {
    return {
      stage: 'top_level',
      topLevelChoice: null,
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    };
  }

  if (serviceKey === 'content') {
    return {
      stage: 'content_mode',
      topLevelChoice: serviceKey,
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    };
  }

  if (serviceKey === 'events' && hasEventBranch) {
    return {
      stage: 'event_navigator',
      topLevelChoice: serviceKey,
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    };
  }

  return {
    stage: 'service_outcome',
    topLevelChoice: serviceKey,
    contentMode: null,
    projectLicenseProgressState: null,
    eventProgressState: null
  };
}

class SmartNavigatorFlow extends Component {
  constructor(props) {
    super(props);

    this.handleTopLevelChoice = this.handleTopLevelChoice.bind(this);
    this.handleContentModeChoice = this.handleContentModeChoice.bind(this);
    this.handleBackToTopLevel = this.handleBackToTopLevel.bind(this);
    this.handleBackToContentModes = this.handleBackToContentModes.bind(this);
    this.handleProjectLicenseProgressChange = this.handleProjectLicenseProgressChange.bind(this);
    this.handleEventProgressChange = this.handleEventProgressChange.bind(this);

    this.state = initialStateForServiceKey(props.initialServiceKey, Boolean(props.eventBranch));
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return SmartNavigatorFlow.defaultProps.i18n[key];
  }

  handleTopLevelChoice(choiceKey, event) {
    event.preventDefault();

    if (choiceKey === 'content') {
      this.setState({
        stage: 'content_mode',
        topLevelChoice: choiceKey,
        contentMode: null,
        projectLicenseProgressState: null,
        eventProgressState: null
      });
      return;
    }

    if (choiceKey === 'events' && this.props.eventBranch) {
      this.setState({
        stage: 'event_navigator',
        topLevelChoice: choiceKey,
        contentMode: null,
        projectLicenseProgressState: null,
        eventProgressState: null
      });
      return;
    }

    this.setState({
      stage: 'service_outcome',
      topLevelChoice: choiceKey,
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    });
  }

  handleContentModeChoice(modeKey, event) {
    event.preventDefault();

    if (modeKey === 'single_project') {
      this.setState({
        stage: 'project_license',
        contentMode: modeKey,
        projectLicenseProgressState: null,
        eventProgressState: null
      });
      return;
    }

    this.setState({
      stage: 'content_outcome',
      contentMode: modeKey,
      projectLicenseProgressState: null,
      eventProgressState: null
    });
  }

  handleBackToTopLevel(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    this.setState({
      stage: 'top_level',
      topLevelChoice: null,
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    });
  }

  handleBackToContentModes(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    this.setState({
      stage: 'content_mode',
      contentMode: null,
      projectLicenseProgressState: null,
      eventProgressState: null
    });
  }

  handleProjectLicenseProgressChange(progressState) {
    this.setState({ projectLicenseProgressState: progressState });
  }

  handleEventProgressChange(progressState) {
    this.setState({ eventProgressState: progressState });
  }

  topLevelOptions() {
    return [
      {
        key: 'content',
        title: this.i18nValue('content_title'),
        description: this.i18nValue('content_description')
      },
      {
        key: 'spaces',
        title: this.i18nValue('spaces_title'),
        description: this.i18nValue('spaces_description')
      },
      {
        key: 'events',
        title: this.i18nValue('events_title'),
        description: this.i18nValue('events_description')
      },
      {
        key: 'courses',
        title: this.i18nValue('courses_title'),
        description: this.i18nValue('courses_description')
      },
      {
        key: 'special_cases',
        title: this.i18nValue('special_cases_title'),
        description: this.i18nValue('special_cases_description')
      }
    ];
  }

  contentModeOptions() {
    return [
      {
        key: 'single_project',
        title: this.i18nValue('single_project_title'),
        description: this.i18nValue('single_project_description')
      },
      {
        key: 'recurring_production',
        title: this.i18nValue('recurring_title'),
        description: this.i18nValue('recurring_description')
      },
      {
        key: 'unsure',
        title: this.i18nValue('unsure_title'),
        description: this.i18nValue('unsure_description')
      }
    ];
  }

  selectedTopLevelOption() {
    return this.topLevelOptions().find((option) => option.key === this.state.topLevelChoice) || null;
  }

  projectLicenseBaseProgressState() {
    const families = this.props.contentBranch?.projectLicenseFlowProps?.families || [];
    const maxConfigQuestionCount = families.reduce((maxCount, family) => Math.max(maxCount, family?.questions?.length || 0), 0);
    const totalSteps = 3 + maxConfigQuestionCount + 1;

    return {
      currentStep: 1,
      totalSteps: totalSteps,
      progressPercent: Math.round((1 / Math.max(totalSteps, 1)) * 100)
    };
  }

  smartNavigatorProgressState() {
    if (this.state.stage === 'event_navigator') {
      return this.state.eventProgressState;
    }

    const baseProgressState = this.state.projectLicenseProgressState || this.projectLicenseBaseProgressState();
    const totalSteps = baseProgressState.totalSteps + 1;

    if (this.state.stage === 'content_mode') {
      return {
        currentStep: 1,
        totalSteps: totalSteps,
        progressPercent: Math.round((1 / Math.max(totalSteps, 1)) * 100)
      };
    }

    if (this.state.stage === 'project_license') {
      const currentStep = Math.min(baseProgressState.currentStep + 1, totalSteps);

      return {
        currentStep: currentStep,
        totalSteps: totalSteps,
        progressPercent: Math.round((currentStep / Math.max(totalSteps, 1)) * 100)
      };
    }

    return null;
  }

  serviceOutcome() {
    const servicePaths = this.props.servicePaths || {};

    return {
      spaces: {
        eyebrow: this.i18nValue('spaces_title'),
        title: this.i18nValue('spaces_result_title'),
        copy: this.i18nValue('spaces_result_copy'),
        primaryLabel: this.i18nValue('spaces_primary_cta'),
        primaryPath: servicePaths.instore
      },
      events: {
        eyebrow: this.i18nValue('events_title'),
        title: this.i18nValue('events_result_title'),
        copy: this.i18nValue('events_result_copy'),
        primaryLabel: this.i18nValue('events_primary_cta'),
        primaryPath: servicePaths.eventTrackPricing || servicePaths.events
      },
      courses: {
        eyebrow: this.i18nValue('courses_title'),
        title: this.i18nValue('courses_result_title'),
        copy: this.i18nValue('courses_result_copy'),
        primaryLabel: this.i18nValue('courses_primary_cta'),
        primaryPath: servicePaths.courses
      },
      special_cases: {
        eyebrow: this.i18nValue('special_cases_title'),
        title: this.i18nValue('special_cases_result_title'),
        copy: this.i18nValue('special_cases_result_copy'),
        primaryLabel: this.i18nValue('special_cases_primary_cta'),
        primaryPath: servicePaths.specialCases
      }
    }[this.state.topLevelChoice] || null;
  }

  contentOutcome() {
    const contentBranch = this.props.contentBranch || {};

    return {
      recurring_production: {
        eyebrow: this.i18nValue('content_title'),
        title: this.i18nValue('recurring_result_title'),
        copy: this.i18nValue('recurring_result_copy'),
        primaryLabel: this.i18nValue('recurring_primary_cta'),
        primaryPath: contentBranch.subscriptionPath,
        secondaryLabel: this.i18nValue('search_music_cta'),
        secondaryPath: contentBranch.searchPath
      },
      unsure: {
        eyebrow: this.i18nValue('content_title'),
        title: this.i18nValue('unsure_result_title'),
        copy: this.i18nValue('unsure_result_copy'),
        primaryLabel: this.i18nValue('unsure_primary_cta'),
        primaryPath: contentBranch.subscriptionPath,
        secondaryLabel: this.i18nValue('unsure_secondary_cta'),
        secondaryAction: this.handleContentModeChoice.bind(this, 'single_project')
      }
    }[this.state.contentMode] || null;
  }

  renderChoiceCards(options, onSelect) {
    return (
      <div className='smart-navigator-flow__choice-grid'>
        {options.map((option) => (
          <a
            href='#'
            key={option.key}
            className='smart-navigator-flow__choice-card'
            onClick={(event) => onSelect(option.key, event)}
          >
            <div className='smart-navigator-flow__choice-title'>{option.title}</div>
            <div className='smart-navigator-flow__choice-copy'>{option.description}</div>
          </a>
        ))}
      </div>
    );
  }

  renderStatusPanel({ eyebrow, title, copy, items = [] }) {
    return (
      <aside className='smart-navigator-flow__panel smart-navigator-flow__panel--status'>
        <div className='smart-navigator-flow__eyebrow'>{eyebrow}</div>
        <div className='smart-navigator-flow__status-title'>{title}</div>
        {copy ? (
          <p className='smart-navigator-flow__status-copy'>{copy}</p>
        ) : null}
        {items.length > 0 ? (
          <dl className='smart-navigator-flow__status-list'>
            {items.map((item) => (
              <div className='smart-navigator-flow__status-item' key={item.label}>
                <dt className='smart-navigator-flow__status-item-label'>{item.label}</dt>
                <dd className='smart-navigator-flow__status-item-value'>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </aside>
    );
  }

  renderOutcomeCard(outcome, onBack) {
    if (!outcome) {
      return null;
    }

    return (
      <div className='smart-navigator-flow__panel smart-navigator-flow__panel--result'>
        <div className='smart-navigator-flow__eyebrow'>{outcome.eyebrow}</div>
        <h2 className='smart-navigator-flow__title'>{outcome.title}</h2>
        <p className='smart-navigator-flow__copy'>{outcome.copy}</p>
        <div className='smart-navigator-flow__actions'>
          {outcome.primaryPath ? (
            <a href={outcome.primaryPath} className='button'>
              {outcome.primaryLabel}
            </a>
          ) : null}
          {outcome.secondaryPath ? (
            <a href={outcome.secondaryPath} className='button button--ghost'>
              {outcome.secondaryLabel}
            </a>
          ) : null}
          {typeof outcome.secondaryAction === 'function' ? (
            <a href='#' className='button button--ghost' onClick={(event) => {
              event.preventDefault();
              outcome.secondaryAction(event);
            }}>
              {outcome.secondaryLabel}
            </a>
          ) : null}
        </div>
        <div className='smart-navigator-flow__panel-footer'>
          <button
            type='button'
            className='inline-license-config__wizard-button inline-license-config__wizard-button--secondary smart-navigator-flow__back-button'
            onClick={onBack}
          >
            {this.i18nValue('back_to_selection')}
          </button>
        </div>
      </div>
    );
  }

  renderProgress() {
    const progressState = this.smartNavigatorProgressState();

    if (!progressState) {
      return null;
    }

    return (
      <div className='license-navigator-v2-flow__progress smart-navigator-flow__progress'>
        <div className='license-navigator-v2-flow__progress-copy'>
          <span>{this.i18nValue('overall_progress')}</span>
          <span>{`${progressState.currentStep} / ${progressState.totalSteps}`}</span>
        </div>
        <div className='license-navigator-v2-flow__progress-track'>
          <div className='license-navigator-v2-flow__progress-fill' style={{ width: `${progressState.progressPercent}%` }}></div>
        </div>
      </div>
    );
  }

  renderTopLevel() {
    return (
      <div className='smart-navigator-flow__panel'>
        <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('eyebrow')}</div>
        <h2 className='smart-navigator-flow__title'>{this.i18nValue('top_level_question')}</h2>
        <p className='smart-navigator-flow__copy'>{this.i18nValue('top_level_copy')}</p>
        {this.renderChoiceCards(this.topLevelOptions(), this.handleTopLevelChoice)}
      </div>
    );
  }

  renderContentMode() {
    const selectedTopLevelOption = this.selectedTopLevelOption();

    return (
      <div className='smart-navigator-flow__branch-layout'>
        <div className='smart-navigator-flow__panel smart-navigator-flow__panel--question'>
          <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('content_title')}</div>
          <h2 className='smart-navigator-flow__title'>{this.i18nValue('content_mode_question')}</h2>
          <p className='smart-navigator-flow__copy'>{this.i18nValue('content_mode_copy')}</p>
          {this.renderChoiceCards(this.contentModeOptions(), this.handleContentModeChoice)}
          <div className='smart-navigator-flow__panel-footer'>
            <button
              type='button'
              className='inline-license-config__wizard-button inline-license-config__wizard-button--secondary smart-navigator-flow__back-button'
              onClick={this.handleBackToTopLevel}
            >
              {this.i18nValue('back_to_selection')}
            </button>
          </div>
        </div>
        {this.renderStatusPanel({
          eyebrow: this.i18nValue('status_eyebrow'),
          title: this.i18nValue('status_title'),
          copy: this.i18nValue('content_mode_status_copy'),
          items: selectedTopLevelOption ? [
            {
              label: this.i18nValue('status_usage_context'),
              value: selectedTopLevelOption.title
            }
          ] : []
        })}
      </div>
    );
  }

  renderProjectLicenseFlow() {
    const projectLicenseFlowProps = this.props.contentBranch?.projectLicenseFlowProps || {};

    return (
      <div className='smart-navigator-flow__project-license'>
        {this.renderProgress()}
        <LicenseNavigatorV2Flow
          {...projectLicenseFlowProps}
          skin='smart_navigator_fullpage'
          showOverallProgress={false}
          onOverallProgressChange={this.handleProjectLicenseProgressChange}
          navigatorSidebarTitle={this.i18nValue('status_title')}
          navigatorSidebarContextItems={[
            {
              label: this.i18nValue('status_usage_context'),
              value: this.i18nValue('content_title')
            }
          ]}
          navigatorSidebarActionLabel={this.i18nValue('back_to_start')}
          onNavigatorSidebarAction={this.handleBackToTopLevel}
          i18n={{
            ...(projectLicenseFlowProps.i18n || {}),
            back_to_navigator: this.i18nValue('back_to_content_modes')
          }}
        />
      </div>
    );
  }

  renderEventNavigatorFlow() {
    const eventBranch = this.props.eventBranch || {};

    return (
      <div className='smart-navigator-flow__event-navigator'>
        {this.renderProgress()}
        <EventNavigatorFlow
          {...eventBranch}
          locale={this.props.locale}
          customerSignedIn={this.props.customerSignedIn}
          onProgressChange={this.handleEventProgressChange}
          onBack={this.handleBackToTopLevel}
          i18n={{
            ...(eventBranch.i18n || {}),
            events_title: this.i18nValue('events_title'),
            back_to_selection: this.i18nValue('back_to_selection')
          }}
        />
      </div>
    );
  }

  renderCurrentStage() {
    if (this.state.stage === 'content_mode') {
      return (
        <>
          {this.renderProgress()}
          {this.renderContentMode()}
        </>
      );
    }

    if (this.state.stage === 'project_license') {
      return this.renderProjectLicenseFlow();
    }

    if (this.state.stage === 'event_navigator') {
      return this.renderEventNavigatorFlow();
    }

    if (this.state.stage === 'content_outcome') {
      return this.renderOutcomeCard(this.contentOutcome(), this.handleBackToContentModes);
    }

    if (this.state.stage === 'service_outcome') {
      return this.renderOutcomeCard(this.serviceOutcome(), this.handleBackToTopLevel);
    }

    return this.renderTopLevel();
  }

  render() {
    return (
      <div className='smart-navigator-flow'>
        {this.renderCurrentStage()}
      </div>
    );
  }
}

SmartNavigatorFlow.defaultProps = {
  locale: 'de',
  initialServiceKey: null,
  contentBranch: {},
  eventBranch: null,
  servicePaths: {},
  i18n: {
    eyebrow: 'Lizenz-Navigator',
    top_level_question: 'Wofür möchten Sie Musik nutzen?',
    top_level_copy: 'Wählen Sie zuerst den Bereich, der am besten zu Ihrem Vorhaben passt. Danach leiten wir Sie in den richtigen nächsten Schritt.',
    content_title: 'Videos, Podcasts, Werbung oder Content',
    content_description: 'Für Content-Projekte mit Musiklizenz oder Musik-Abo als möglichem nächsten Schritt.',
    spaces_title: 'Geschäftsräume oder Praxis',
    spaces_description: 'Für Laden, Gastronomie, Praxis, Wartebereich oder andere gewerbliche Raumwiedergabe.',
    events_title: 'Events',
    events_description: 'Für einzelne Veranstaltungen, Event-Pakete und passende Event-Lizenzpfade.',
    courses_title: 'Kurse',
    courses_description: 'Für Kurse, Memberships und andere Lern- oder Trainingsformate.',
    special_cases_title: 'Spezialfälle',
    special_cases_description: 'Für Vorhaben, die nicht sauber in die Standardpfade passen oder individuell geklärt werden sollten.',
    content_mode_question: 'Geht es um ein einzelnes Projekt oder um laufende Content-Produktion?',
    content_mode_copy: 'So finden wir schneller den passenden Weg für Ihr Vorhaben.',
    single_project_title: 'Für ein einzelnes Projekt',
    single_project_description: 'Zum Beispiel ein Video, Podcast, Trailer oder ein klar abgegrenzter Projekt-Rahmen.',
    recurring_title: 'Für laufende oder wiederkehrende Produktion',
    recurring_description: 'Für regelmäßige Content-Produktion, mehrere Projekte oder wiederkehrende Formate.',
    unsure_title: 'Ich bin noch unsicher',
    unsure_description: 'Wenn Sie noch nicht wissen, ob eher Projekt-Lizenz oder Musik-Abo passt.',
    project_license_branch_title: 'Passende Projekt-Lizenz finden',
    project_license_branch_copy: 'Im nächsten Schritt finden Sie zuerst die passende Lizenzfamilie und konfigurieren Ihre Lizenz danach direkt passend zu Ihrem Projekt.',
    recurring_result_title: 'Das Musik-Abo ist hier oft der sinnvollere nächste Schritt',
    recurring_result_copy: 'Wenn Sie laufend produzieren oder mehrere Projekte planen, ist ein Musik-Abo meist der bessere Einstieg als eine einzelne Projekt-Lizenz.',
    recurring_primary_cta: 'Zum Musik-Abo',
    unsure_result_title: 'Wir können jetzt in beide Richtungen weitergehen',
    unsure_result_copy: 'Wenn Sie eher ein einzelnes Projekt planen, starten Sie mit der Projekt-Lizenz. Wenn Sie regelmäßig produzieren, sehen Sie sich zuerst das Musik-Abo an.',
    unsure_primary_cta: 'Musik-Abo ansehen',
    unsure_secondary_cta: 'Projekt-Lizenz prüfen',
    search_music_cta: 'Passende Musik suchen',
    spaces_result_title: 'Der Instore-/Praxis-Pfad passt hier besser',
    spaces_result_copy: 'Für Geschäftsräume, Praxis, Wartebereiche und ähnliche gewerbliche Raumwiedergabe ist der Instore-Bereich der passendere nächste Schritt.',
    spaces_primary_cta: 'Zum Instore-Bereich',
    events_result_title: 'Der Event-Pfad passt hier besser',
    events_result_copy: 'Für Veranstaltungen, Event-Pakete und Event-Lizenzierungen ist der Event-Bereich der passende nächste Schritt.',
    events_primary_cta: 'Zum Event-Bereich',
    courses_result_title: 'Der Kurs-Bereich passt hier besser',
    courses_result_copy: 'Für Kurse, Memberships und ähnliche Lernformate führen wir Sie besser direkt in den Kurs-Bereich.',
    courses_primary_cta: 'Zum Kurs-Bereich',
    special_cases_result_title: 'Hier ist eine individuelle Einordnung sinnvoll',
    special_cases_result_copy: 'Wenn Ihr Vorhaben nicht sauber in die Standardpfade passt, ist eine direkte Anfrage aktuell der sinnvollste nächste Schritt.',
    special_cases_primary_cta: 'Kontakt aufnehmen',
    overall_progress: 'Gesamtfortschritt',
    back_to_selection: '← Zurück',
    back_to_start: '← Zurück zum Anfang',
    back_to_content_modes: '← Zurück zur Content-Auswahl',
    status_eyebrow: 'Aktueller Stand',
    status_title: 'Ihr aktueller Stand',
    status_usage_context: 'Nutzungskontext',
    content_mode_status_copy: 'Wir grenzen jetzt ein, ob es um ein einzelnes Projekt oder um laufende Content-Produktion geht.'
  }
};

let elements = document.querySelectorAll('[data-react-component=SmartNavigatorFlow]');

elements.forEach(function(element) {
  let reactProps = parseReactProps(element.dataset.reactProps);

  ReactDOM.render(
    <React.StrictMode>
      <SmartNavigatorFlow {...reactProps} />
    </React.StrictMode>,
    element
  );
});

export default SmartNavigatorFlow;
